/**
 * TourRunner — Config-driven guided tour engine.
 *
 * Responsibilities:
 *   1. Fetch onboarding context + tour state from backend.
 *   2. Compute available / completed / current step from config.
 *   3. Render desktop spotlight overlay OR mobile bottom sheet.
 *   4. Handle dismiss, navigate, back, next, complete.
 *
 * Usage: <TourRunner tourId="activation" /> inside Layout.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { tourRegistry } from "./tourRegistry";
import { X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Helpers ─────────────────────────────────────────────────────── */

function getToken() {
  try {
    return localStorage.getItem("access_token");
  } catch {
    return null;
  }
}

async function apiFetch(path, opts = {}) {
  const token = getToken();
  if (!token) return null;
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...opts.headers },
  });
  if (!res.ok) return null;
  return res.json();
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

/* ── Spotlight Overlay (desktop) ─────────────────────────────────── */

function SpotlightOverlay({ targetRect, onDismiss, children }) {
  const overlayBg = "rgba(0, 0, 0, 0.6)";

  if (!targetRect) {
    return (
      <div className="fixed inset-0 z-[9998]" onClick={onDismiss}>
        <div className="absolute inset-0" style={{ backgroundColor: overlayBg }} />
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999]"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    );
  }

  const pad = 8;
  const r = {
    x: targetRect.x - pad,
    y: targetRect.y - pad,
    w: targetRect.width + pad * 2,
    h: targetRect.height + pad * 2,
  };

  // Position tooltip near target
  const tooltipStyle = {};
  const viewW = window.innerWidth;
  const viewH = window.innerHeight;

  if (r.x + r.w + 340 < viewW) {
    tooltipStyle.left = r.x + r.w + 12;
    tooltipStyle.top = Math.max(16, Math.min(r.y, viewH - 280));
  } else if (r.x - 340 > 0) {
    tooltipStyle.left = r.x - 340 - 12;
    tooltipStyle.top = Math.max(16, Math.min(r.y, viewH - 280));
  } else {
    tooltipStyle.left = Math.max(16, r.x);
    tooltipStyle.top = r.y + r.h + 12;
  }

  return (
    <div className="fixed inset-0 z-[9998]" onClick={onDismiss}>
      {/* 4-panel overlay: top, left, right, bottom around the spotlight hole */}
      <div className="absolute transition-all duration-300 ease-out" style={{ top: 0, left: 0, right: 0, height: r.y, backgroundColor: overlayBg }} />
      <div className="absolute transition-all duration-300 ease-out" style={{ top: r.y, left: 0, width: r.x, height: r.h, backgroundColor: overlayBg }} />
      <div className="absolute transition-all duration-300 ease-out" style={{ top: r.y, left: r.x + r.w, right: 0, height: r.h, backgroundColor: overlayBg }} />
      <div className="absolute transition-all duration-300 ease-out" style={{ top: r.y + r.h, left: 0, right: 0, bottom: 0, backgroundColor: overlayBg }} />
      {/* Highlight ring */}
      <div
        className="absolute rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-transparent pointer-events-none transition-all duration-300 ease-out"
        style={{ left: r.x, top: r.y, width: r.w, height: r.h }}
      />
      {/* Tooltip panel */}
      <div
        className="absolute z-[9999] w-[320px]"
        style={tooltipStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/* ── Mobile Bottom Sheet ─────────────────────────────────────────── */

function MobileSheet({ onDismiss, children }) {
  return (
    <div className="fixed inset-0 z-[9998]" onClick={onDismiss}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="absolute bottom-0 left-0 right-0 z-[9999] animate-in slide-in-from-bottom duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/* ── Tooltip Card (shared between desktop + mobile) ──────────────── */

function TourCard({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onBack,
  onDismiss,
  isMobile,
}) {
  return (
    <div
      className={`bg-white shadow-xl border border-border ${
        isMobile
          ? "rounded-t-xl p-5 pb-8 w-full max-w-full"
          : "rounded-xl p-5"
      }`}
      data-testid="tour-card"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <span className="text-xs font-medium text-muted-foreground">
            Step {stepIndex + 1} of {totalSteps}
          </span>
        </div>
        <button
          onClick={onDismiss}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 -mr-1 -mt-1"
          data-testid="tour-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <h3 className="text-base font-semibold text-foreground mb-1.5">
        {step.title}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
        {step.body}
      </p>

      {/* Progress dots */}
      <div className="flex gap-1.5 mb-4">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all ${
              i === stepIndex
                ? "w-6 bg-primary"
                : i < stepIndex
                ? "w-1.5 bg-primary/40"
                : "w-1.5 bg-muted"
            }`}
          />
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={onDismiss}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="tour-exit-btn"
        >
          Exit Tour
        </button>
        <div className="flex gap-2">
          {stepIndex > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              data-testid="tour-back-btn"
            >
              <ChevronLeft className="w-3.5 h-3.5 mr-1" />
              Back
            </Button>
          )}
          <Button size="sm" onClick={onNext} data-testid="tour-next-btn">
            {step.ctaLabel || "Next"}
            <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── Main TourRunner ─────────────────────────────────────────────── */

export function TourRunner({ tourId = "activation" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 767px)");

  const [ctx, setCtx] = useState(null);
  const [tourState, setTourState] = useState(null);
  const [manualStepIdx, setManualStepIdx] = useState(null);
  const [visible, setVisible] = useState(false);
  const [targetRect, setTargetRect] = useState(null);
  const rafRef = useRef(null);

  const tour = tourRegistry[tourId];

  // ── Data fetching ──────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    const [c, s] = await Promise.all([
      apiFetch("/api/onboarding/context"),
      apiFetch(`/api/onboarding/tour-state?tour_id=${tourId}`),
    ]);
    if (c) setCtx(c);
    if (s) setTourState(s);
  }, [tourId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Re-fetch context on route change (step may have been completed)
  useEffect(() => {
    if (visible) fetchData();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Compute steps ──────────────────────────────────────────────
  const { availableSteps, currentStepIdx } = useMemo(() => {
    if (!tour || !ctx) return { availableSteps: [], currentStepIdx: -1 };

    const avail = tour.steps.filter((s) => s.isAvailable(ctx));
    const firstIncomplete = avail.findIndex((s) => !s.isComplete(ctx));
    return { availableSteps: avail, currentStepIdx: firstIncomplete };
  }, [tour, ctx]);

  const activeIdx = manualStepIdx !== null ? manualStepIdx : currentStepIdx;
  const currentStep = availableSteps[activeIdx];

  // ── Auto-show logic ────────────────────────────────────────────
  useEffect(() => {
    if (!tourState || !ctx || !tour) return;

    // Already dismissed or completed → don't auto-show
    if (tourState.dismissed_at || tourState.completed_at) return;
    if (!tourState.auto_show_enabled) return;

    // All steps complete → mark complete, don't show
    if (currentStepIdx === -1 && availableSteps.length > 0) {
      apiFetch("/api/onboarding/tour-state", {
        method: "POST",
        body: JSON.stringify({ tour_id: tourId, action: "complete", tour_version: tour.version }),
      });
      return;
    }

    if (currentStepIdx >= 0) {
      setVisible(true);
    }
  }, [tourState, ctx, currentStepIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Track target element ───────────────────────────────────────
  useEffect(() => {
    if (!visible || !currentStep || isMobile) {
      setTargetRect(null);
      return;
    }

    // Expand nav section if needed (reusable for any step)
    if (currentStep.ensureNavOpen && window.__tourExpandNav) {
      window.__tourExpandNav(currentStep.ensureNavOpen);
    }

    const track = () => {
      const sel = currentStep.targetSelector;
      const fallback = currentStep.fallbackSelector;
      let el = sel ? document.querySelector(sel) : null;
      if (!el && fallback) el = document.querySelector(fallback);
      if (!el) {
        setTargetRect(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      setTargetRect({ x: rect.x, y: rect.y, width: rect.width, height: rect.height });
      rafRef.current = requestAnimationFrame(track);
    };

    // Delay to let nav expand animation finish before measuring
    const timer = setTimeout(track, 350);

    return () => {
      clearTimeout(timer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [visible, currentStep, isMobile, location.pathname]);

  // ── Actions ────────────────────────────────────────────────────
  const dismiss = async () => {
    setVisible(false);
    setManualStepIdx(null);
    await apiFetch("/api/onboarding/tour-state", {
      method: "POST",
      body: JSON.stringify({ tour_id: tourId, action: "dismiss", tour_version: tour.version }),
    });
    setTourState((prev) => (prev ? { ...prev, dismissed_at: new Date().toISOString() } : prev));
  };

  const goNext = () => {
    // Navigate to the step's route
    if (currentStep?.ctaAction === "navigate" && currentStep.route) {
      const route = typeof currentStep.route === "function" ? currentStep.route(ctx) : currentStep.route;
      navigate(route);
    }

    // Advance to next step
    const nextIdx = activeIdx + 1;
    if (nextIdx < availableSteps.length) {
      setManualStepIdx(nextIdx);
    } else {
      // Tour complete
      setVisible(false);
      setManualStepIdx(null);
      apiFetch("/api/onboarding/tour-state", {
        method: "POST",
        body: JSON.stringify({ tour_id: tourId, action: "complete", tour_version: tour.version }),
      });
      setTourState((prev) => (prev ? { ...prev, completed_at: new Date().toISOString() } : prev));
    }
  };

  const goBack = () => {
    if (activeIdx > 0) {
      setManualStepIdx(activeIdx - 1);
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  if (!visible || !currentStep) return null;

  const card = (
    <TourCard
      step={currentStep}
      stepIndex={activeIdx}
      totalSteps={tour.steps.length}
      onNext={goNext}
      onBack={goBack}
      onDismiss={dismiss}
      isMobile={isMobile}
    />
  );

  const overlay = isMobile ? (
    <MobileSheet onDismiss={dismiss}>{card}</MobileSheet>
  ) : (
    <SpotlightOverlay targetRect={targetRect} onDismiss={dismiss}>
      {card}
    </SpotlightOverlay>
  );

  return createPortal(overlay, document.body);
}

export default TourRunner;
